/**
 * <search-results>
 *
 * Applies a filter the moment it is ticked. The drawer
 * (component-facets-drawer, rendered with `auto_apply`) announces the filtered
 * URL; this re-requests the section through the Section Rendering API with it
 * and swaps the results in place, so the drawer stays open for the next tick.
 *
 * One swap of this element's content covers everything a filter changes: the
 * heading count, the controls row and its chips, the grid, the pager and the
 * zero-results bands. The drawer is a sibling, not a child, so its groups are
 * handed over separately through its own refresh().
 *
 * A failed request falls back to navigating to the same URL, so a tick still
 * filters rather than leaving the drawer looking broken.
 */
if (!customElements.get('search-results')) {
  customElements.define(
    'search-results',
    class SearchResults extends HTMLElement {
      abortController = undefined;

      constructor() {
        super();
        this.onApply = this.onApply.bind(this);
        this.onPopState = this.onPopState.bind(this);
      }

      connectedCallback() {
        /* What the markup on screen was rendered for, so the back button can
           tell whether it needs to fetch. */
        this.dataset.renderedUrl = `${window.location.pathname}${window.location.search}`;

        document.addEventListener('facets-drawer:apply', this.onApply);
        window.addEventListener('popstate', this.onPopState);
      }

      disconnectedCallback() {
        document.removeEventListener('facets-drawer:apply', this.onApply);
        window.removeEventListener('popstate', this.onPopState);
        this.abortController?.abort();
      }

      async onApply(event) {
        event.preventDefault();
        const { url } = event.detail;

        if (await this.render(url)) {
          history.pushState({}, '', `${url.pathname}${url.search}`);
        }
      }

      async onPopState() {
        /* A fragment-only history entry leaves the query alone, and the results
           on screen already match it. */
        if (this.dataset.renderedUrl === `${window.location.pathname}${window.location.search}`) return;

        await this.render(new URL(window.location.href));
      }

      async render(url) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        const request = new URL(url.pathname, window.location.origin);
        request.search = url.search;
        request.searchParams.set('section_id', this.dataset.section);

        this.setAttribute('aria-busy', 'true');

        try {
          const response = await fetch(request, { signal: this.abortController.signal });
          if (!response.ok) throw new Error(`Section render failed: ${response.status}`);

          const html = new DOMParser().parseFromString(await response.text(), 'text/html');
          const fresh = html.getElementById(this.id);
          if (!fresh) throw new Error(`No #${this.id} in the rendered section`);

          this.replaceChildren(...fresh.childNodes);
          this.dataset.renderedUrl = `${url.pathname}${url.search}`;
          this.querySelector('grid-controls')?.syncPriceFilter(url);
          this.querySelector('grid-controls')?.syncBrandFilter(url);

          /* A result set with no facets renders no drawer. The one on screen is
             kept, so the shopper can still untick whatever emptied the results. */
          const freshDrawer = html.querySelector('facets-drawer');
          if (freshDrawer) document.querySelector('facets-drawer')?.refresh(freshDrawer, url);

          this.removeAttribute('aria-busy');
          return true;
        } catch (error) {
          if (error.name === 'AbortError') return false;

          console.error(error);
          window.location.assign(url);
          return false;
        }
      }
    },
  );
}

/**
 * <search-teams>
 *
 * League filtering for the All Teams band on the zero-results search page
 * (Figma 326:12728 desktop, 326:13897 mobile).
 *
 * Every team is already in the grid — see the comment above the loop in
 * `sections/search.liquid` for why — so narrowing to a league never costs a
 * round trip, and the band stays a fixed-size teaser: at most `data-reveal`
 * cells, whichever league is selected.
 *
 * WHY IT DETACHES RATHER THAN HIDES
 * ---------------------------------
 * The mobile frame draws hairlines between the grid's columns and rows, and
 * `section-search.css` places them with `:nth-child`. Structural selectors
 * count hidden siblings, so hiding the 111 teams that are not on show would
 * leave the dividers computed against a grid the reader cannot see. Detaching
 * keeps the DOM and the design in agreement and needs no column count in here.
 *
 * Nothing is re-rendered on the way back: the cells are held in `this.cells`
 * from first connect, so a detached team is one `append` away from returning.
 *
 * This is deliberately NOT `<team-directory>`, which filters the same
 * metaobject by the same field. That element also owns sorting, a live count,
 * progressive "load more" and an external filter event, and re-marks row
 * positions on every breakpoint change — none of which a teaser has. Sharing
 * it would mean widening a section-owned file to a second section (and so
 * renaming it) to inherit behaviour this band must not have.
 */
if (!customElements.get('search-teams')) {
  customElements.define(
    'search-teams',
    class SearchTeams extends HTMLElement {
      constructor() {
        super();
        this.onLeagueClick = this.onLeagueClick.bind(this);
        this.league = '';
      }

      connectedCallback() {
        this.grid = this.querySelector('[data-search-teams-grid]');
        if (!this.grid) return;

        this.cells = Array.from(this.grid.children);
        this.buttons = Array.from(this.querySelectorAll('[data-league-filter]'));

        for (const button of this.buttons) {
          button.addEventListener('click', this.onLeagueClick);
        }

        this.apply();
      }

      disconnectedCallback() {
        for (const button of this.buttons || []) {
          button.removeEventListener('click', this.onLeagueClick);
        }
      }

      /** How many cells a league may show. Falls back to every team. */
      get reveal() {
        return Number(this.dataset.reveal) || this.cells.length;
      }

      onLeagueClick(event) {
        this.setLeague(event.currentTarget.dataset.leagueFilter);
      }

      /**
       * Pressed state is matched on the value rather than on element identity,
       * so the All pill — whose `data-league-filter` is deliberately empty —
       * is pressed by the same line as every other one.
       */
      setLeague(league) {
        this.league = league || '';

        for (const button of this.buttons) {
          button.setAttribute('aria-pressed', String((button.dataset.leagueFilter || '') === this.league));
        }

        this.apply();
      }

      apply() {
        const matching = this.cells.filter((cell) => !this.league || cell.dataset.league === this.league);
        const shown = matching.slice(0, this.reveal);

        // The server marks the teaser by hiding the overflow, so that a visitor
        // without JavaScript still gets `data-reveal` teams rather than all of
        // them. Whatever is on show here is on show for real.
        for (const cell of shown) {
          cell.hidden = false;
        }

        // Detaches everything absent from the list in the same pass.
        this.grid.replaceChildren(...shown);
      }
    },
  );
}
